import { Check } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * ProgressTimeline — a horizontal, milestone-style timeline: a connecting rail
 * with a progress fill, a node per step (complete / active / upcoming), and a
 * two-line label (meta + title) under each node.
 *
 * Distinct from `StepIndicator` (equal-width segmented bars, single visible
 * label) and `SegmentedProgress` (a plain segmented bar). Reach for this when
 * each milestone carries its own dated caption — e.g. an enrollment window
 * ("Window opens · Jul 1" → "Choose your benefits" → "Coverage starts").
 *
 * Purely presentational: the caller resolves each step's `state` and copy.
 * The rail fill is derived from the step states — filled up to the active
 * step, or fully filled when every step is complete.
 *
 * Lifted from the Benefits Wallet enrollment surfaces (was
 * `apps/wallet/.../WalletEnrollmentSurfaces` + `.wallet-enrollment-timeline`).
 */

export type ProgressTimelineStepState = "complete" | "active" | "upcoming";

export interface ProgressTimelineStep {
  /** Stable key. */
  id: string;
  /** Primary line under the node. */
  title: React.ReactNode;
  /** Supporting line above the title (date, hint). */
  meta?: React.ReactNode;
  /** Milestone state. Drives the node style and the rail fill. */
  state: ProgressTimelineStepState;
}

export interface ProgressTimelineProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Ordered milestones, first → last. */
  steps: ProgressTimelineStep[];
  /** Accessible label for the timeline group. Default `"Progress"`. */
  ariaLabel?: string;
}

/**
 * Rail fill percentage across the node centers. The rail spans from the first
 * node center to the last; fill reaches the active node, or 100% when all
 * steps are complete, or 0% when the active step is first / none started.
 */
function railProgress(steps: ProgressTimelineStep[]): number {
  if (steps.length <= 1) return 0;
  const activeIndex = steps.findIndex((s) => s.state === "active");
  if (activeIndex === -1) {
    return steps.every((s) => s.state === "complete") ? 100 : 0;
  }
  return (activeIndex / (steps.length - 1)) * 100;
}

export const ProgressTimeline = React.forwardRef<HTMLDivElement, ProgressTimelineProps>(
  function ProgressTimeline({ ariaLabel = "Progress", className, steps, ...props }, ref) {
    const progress = railProgress(steps);

    return (
      <div ref={ref} className={cx("cw-v2-progress-timeline", className)} {...props}>
        <div className="cw-v2-progress-timeline__scroll">
          <div className="cw-v2-progress-timeline__rail" aria-hidden>
            <div className="cw-v2-progress-timeline__track">
              <div
                className="cw-v2-progress-timeline__track-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          {/* The <ol> is the semantic group; label it rather than the layout wrapper. */}
          <ol
            aria-label={ariaLabel}
            className="cw-v2-progress-timeline__steps"
            style={{ "--cw-v2-progress-timeline-count": steps.length } as React.CSSProperties}
          >
            {steps.map((step) => (
              <li
                key={step.id}
                className="cw-v2-progress-timeline__step"
                aria-current={step.state === "active" ? "step" : undefined}
              >
                <span
                  className={cx(
                    "cw-v2-progress-timeline__node",
                    `cw-v2-progress-timeline__node--${step.state}`,
                  )}
                  aria-hidden
                >
                  {step.state === "complete" ? (
                    <Check className="cw-v2-progress-timeline__check" size={12} />
                  ) : null}
                </span>
                <span className="cw-v2-progress-timeline__labels">
                  {step.meta ? (
                    <span
                      className={cx(
                        "cw-v2-progress-timeline__meta",
                        step.state === "active" && "cw-v2-progress-timeline__meta--active",
                      )}
                    >
                      {step.meta}
                    </span>
                  ) : null}
                  <span className="cw-v2-progress-timeline__title">{step.title}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  },
);
ProgressTimeline.displayName = "ProgressTimeline";
